/**
 * Query-tab results, keyed by tab id, surviving component unmounts (switching
 * to Collections unmounts DbManagerResultsTab entirely). Kept out of zustand:
 * result sets can be large and must never enter persisted snapshots.
 *
 * Lifecycle: written on every query completion, pruned to the open tab set on
 * tab switches AND on workspace-snapshot hydration, cleared wholesale on
 * tabsStore.reset() (logout).
 *
 * Entry shape (multi-result-set era):
 *   { results: DbQueryResult[] | null, selectedIndex: number, error: string|null }
 * where DbQueryResult = { columns, rows, affectedRows, lastInsertId } — one
 * element per statement of the executed script, `selectedIndex` being the
 * result-set switcher position so a tab switch restores the same view.
 * Readers should tolerate the legacy single-result shape { result, error }.
 *
 * Bounded by total cell count, evicting least-recently-used tabs. Pruning to
 * the open tab set alone is not a bound: twenty open query tabs each holding a
 * capped 10,000-row result is 200,000 rows the session never gives back, and
 * nothing about staying on one tab for an hour releases the other nineteen.
 * An evicted tab re-runs its query when you return to it, which is the same
 * thing that happens after a restart.
 */

// Roughly three full-size results (the backend caps a query at 10,000 rows).
// Counting cells rather than bytes because the alternative is walking every
// value on every write; cells track the real cost closely enough to bound it.
export const MAX_CACHED_CELLS = 1_000_000;

const cache = new Map(); // tab.id -> { value, cells }
let totalCells = 0;

/** Cells an entry holds, summed across its result sets. */
function measure(value) {
  const sets = value?.results || (value?.result ? [value.result] : null);
  if (!Array.isArray(sets)) return 0;
  let cells = 0;
  for (const set of sets) {
    const rows = set?.rows?.length || 0;
    const cols = set?.columns?.length || 0;
    cells += rows * cols;
  }
  return cells;
}

/**
 * Evict from the front (least recently used) until the total fits.
 *
 * The newest entry is never evicted even when it alone exceeds the cap: it is
 * the result the user is looking at, and dropping it would blank the tab that
 * just finished querying.
 */
function evictToFit() {
  for (const key of cache.keys()) {
    if (totalCells <= MAX_CACHED_CELLS || cache.size <= 1) break;
    totalCells -= cache.get(key).cells;
    cache.delete(key);
  }
}

export function getDbQueryResult(tabId) {
  const entry = cache.get(tabId);
  if (!entry) return null;
  // Re-insert to mark as most recently used — Map.set on an existing key keeps
  // its original position, so the delete is what actually moves it.
  cache.delete(tabId);
  cache.set(tabId, entry);
  return entry.value;
}

export function setDbQueryResult(tabId, value) {
  const previous = cache.get(tabId);
  if (previous) totalCells -= previous.cells;
  cache.delete(tabId);

  const cells = measure(value);
  cache.set(tabId, { value, cells });
  totalCells += cells;
  evictToFit();
}

/** Forget one tab's cached result, so its next arrival fetches instead of restoring. */
export function dropDbQueryResult(tabId) {
  const entry = cache.get(tabId);
  if (!entry) return;
  totalCells -= entry.cells;
  cache.delete(tabId);
}

export function pruneDbQueryResultCache(keepIds) {
  const keep = keepIds instanceof Set ? keepIds : new Set(keepIds);
  for (const key of [...cache.keys()]) {
    if (!keep.has(key)) {
      totalCells -= cache.get(key).cells;
      cache.delete(key);
    }
  }
}

export function clearDbQueryResultCache() {
  cache.clear();
  totalCells = 0;
}

/** Test/diagnostic view of what the cache is holding. */
export function dbQueryResultCacheStats() {
  return { entries: cache.size, cells: totalCells };
}
